import { Backdrop, SvgIcon, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { NavigationDrawer } from '@/widgets/NavigationDrawer';
import { Layout, RequestNewLink, Title } from '@/features/auth';
import {
  selectActiveOrganizationId,
  selectIsAuthorized,
  selectIsDisabled,
  selectIsSessionLoading,
  selectIsSessionRequested,
  selectRequiresOnboarding,
  setupProfile,
  useActivityTracking,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { Analytics, toSafeRedirectPath } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Spinner, Page, Panel, NoItemsPlaceholder } from '@/shared/ui';
import { useCrossTabAuthReload } from '../../model/useCrossTabAuthReload';
import { UsageTracker } from '../UsageTracker/UsageTracker';

export function AuthGuard() {
  const dispath = useAppDispatch();
  const isSessionRequested = useAppSelector(selectIsSessionRequested);
  const isSessionLoading = useAppSelector(selectIsSessionLoading);
  const isAuthorized = useAppSelector(selectIsAuthorized);
  const isDisabled = useAppSelector(selectIsDisabled);
  const requiresOnboarding = useAppSelector(selectRequiresOnboarding);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const location = useLocation();
  const from = toSafeRedirectPath(`${location.pathname}${location.search}`);

  useActivityTracking(isAuthorized);
  useCrossTabAuthReload();

  useEffect(() => {
    if (isSessionRequested) {
      return;
    }

    dispath(setupProfile());
  }, [isSessionRequested, dispath]);

  if (isSessionLoading || !isSessionRequested) {
    return (
      <Backdrop open>
        <Spinner />
      </Backdrop>
    );
  } else if (!isAuthorized) {
    if (isExpiredOTP(location.hash)) {
      Analytics.sendFeatureUsage('expired_auth_link', 'view_navigated');

      return (
        <Layout>
          <Title>Link expired</Title>
          <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
            The link you clicked has expired. Enter your email address below to
            receive a new link.
          </Typography>
          <RequestNewLink />
        </Layout>
      );
    }

    return <Navigate to={ROUTES.signin} state={{ from }} replace />;
  } else if (isDisabled) {
    return (
      <Page.Layout
        navigationSlot={<NavigationDrawer />}
        slotProps={{
          content: {
            sx: {
              display: 'flex',
              gap: 1,
            },
          },
        }}
      >
        <Panel
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NoItemsPlaceholder
            icon={
              <SvgIcon
                sx={{ width: 100, height: 100, color: 'text.secondary' }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    ry="2"
                  ></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </SvgIcon>
            }
            title="Account disabled"
            description="Please contact your administrator to activate your account."
          />
        </Panel>
      </Page.Layout>
    );
  } else if (requiresOnboarding && location.pathname !== ROUTES.acceptInvite) {
    return <Navigate to={ROUTES.acceptInvite} state={{ from }} replace />;
  }

  return (
    <>
      <UsageTracker organizationId={activeOrganizationId} />
      <Outlet key={activeOrganizationId} />
    </>
  );
}

const isExpiredOTP = (hash: string) => {
  const parsedHash = new URLSearchParams(hash.substring(1));

  return (
    parsedHash.get('error') === 'access_denied' &&
    parsedHash.get('error_code') === 'otp_expired'
  );
};
